import { useMemo, useState } from 'react';
import type { EntityImportConfig, ImportPreviewRow, ReviewTab } from '../types';

const PAGE_SIZE = 25;

interface ReviewStepProps {
  config: EntityImportConfig;
  rows: ImportPreviewRow[];
  onToggleSkip: (rowNumber: number) => void;
}

export default function ReviewStep({ config, rows, onToggleSkip }: ReviewStepProps) {
  const [tab, setTab] = useState<ReviewTab>('ready');
  const [page, setPage] = useState(0);

  const readyCount = rows.filter((r) => r.status === 'ready').length;
  const skippedCount = rows.filter((r) => r.status === 'skipped').length;

  const filtered = useMemo(
    () => rows.filter((r) => r.status === tab),
    [rows, tab]
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const fieldnames = config.fields.map((f) => f.fieldname);

  const setTabSafe = (next: ReviewTab) => {
    setTab(next);
    setPage(0);
  };

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-lg font-bold text-gray-900 mb-1">Step 3: Review Rows</h4>
        <p className="text-gray-600 text-sm">
          Check ready rows and skipped rows. You can manually skip or restore a row before validation.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setTabSafe('ready')}
          className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${
            tab === 'ready'
              ? 'bg-primary-50 border-primary-600 text-primary-700'
              : 'bg-white border-gray-200 text-gray-700'
          }`}
        >
          Ready to Import ({readyCount})
        </button>
        <button
          type="button"
          onClick={() => setTabSafe('skipped')}
          className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${
            tab === 'skipped'
              ? 'bg-primary-50 border-primary-600 text-primary-700'
              : 'bg-white border-gray-200 text-gray-700'
          }`}
        >
          Skipped ({skippedCount})
        </button>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[420px]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-gray-900 whitespace-nowrap">Row</th>
              {config.fields.map((f) => (
                <th
                  key={f.fieldname}
                  className="text-left px-3 py-2 font-semibold text-gray-900 whitespace-nowrap"
                >
                  {f.label}
                </th>
              ))}
              <th className="text-left px-3 py-2 font-semibold text-gray-900">Status</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-900">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={fieldnames.length + 3} className="px-3 py-8 text-center text-gray-400">
                  No rows in this tab
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr key={row.rowNumber} className={row.status === 'skipped' ? 'bg-amber-50/40' : ''}>
                  <td className="px-3 py-2 text-gray-600">{row.rowNumber}</td>
                  {fieldnames.map((fn) => (
                    <td
                      key={fn}
                      className="px-3 py-2 text-gray-800 max-w-[180px] break-words align-top"
                    >
                      {row.fields[fn] || '—'}
                    </td>
                  ))}
                  <td className="px-3 py-2 align-top">
                    {row.status === 'ready' ? (
                      <span className="text-green-700 text-xs font-medium">Ready</span>
                    ) : (
                      <span className="text-amber-700 text-xs font-medium">
                        {row.skipReasonLabel || 'Skipped'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <button
                      type="button"
                      onClick={() => onToggleSkip(row.rowNumber)}
                      className="text-xs font-medium text-primary-700 hover:underline"
                    >
                      {row.status === 'ready' ? 'Skip' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="btn-secondary disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {page + 1} of {pageCount}
          </span>
          <button
            type="button"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className="btn-secondary disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
